/**
 * CJS Auto-Capture Only Implementation
 * Minimal implementation focused only on Auto-Capture functionality
 * This works alongside your existing Adobe Analytics - it does not replace it
 */

import { getEnvironmentConstant } from './utilities';
import { ENVIRONMENT } from './constants';

class CJSAutoCapture {
  constructor() {
    this.isInitialized = false;
    this.cjsTracker = null;
  }

  /**
   * Initialize CJS Auto-Capture
   * @param {Object} envContext - Environment context
   */
  async initialize(envContext) {
    try {
      const env = envContext[ENVIRONMENT];
      // Check if CJS is enabled
      const isEnabled = getEnvironmentConstant({ key: 'CJS_ENABLED', env }) !== 'false';
      if (!isEnabled) {
        console.info('CJS Auto-Capture disabled via configuration');
        return;
      }

      // Dynamically load the required CJS SDK scripts
      await this.loadCJSScripts(env);

      // Wait for CJS SDK to be available
      await this.waitForCJSSDK();

      // Initialize CJS Tracker
      await this.initializeCjsTracker(env);

      this.isInitialized = true;
      console.info('CJS Auto-Capture initialized successfully');
    } catch (error) {
      console.error('Failed to initialize CJS Auto-Capture:', error.message);
      // Try one more time after window load event if scripts are still loading
      if (!this.isInitialized && document.readyState !== 'complete') {
        console.info('Attempting CJS initialization after window load...');
        window.addEventListener('load', () => {
          setTimeout(async () => {
            try {
              const env = envContext[ENVIRONMENT];
              await this.loadCJSScripts(env);
              await this.waitForCJSSDK();
              await this.initializeCjsTracker(env);
              this.isInitialized = true;
              console.info('CJS Auto-Capture initialized successfully on retry');
            } catch (retryError) {
              console.warn('CJS Auto-Capture retry failed:', retryError.message);
            }
          }, 1000);
        });
      }
      // Don't throw - we don't want to break the app if CJS fails
    }
  }

  /**
   * Load a single script dynamically
   * @param {string} src - Script source URL
   * @param {Function} onLoad - Optional onload callback
   * @param {Function} onError - Optional onerror callback
   */
  // eslint-disable-next-line class-methods-use-this
  loadScript(src, onLoad, onError) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.async = true;
    script.onload = onLoad;
    script.onerror = onError;
    document.head.appendChild(script);
  }

  /**
   * Load CJS SDK scripts dynamically
   * @param {string} env - Environment
   */
  async loadCJSScripts(env) {
    console.info(`Loading CJS SDK scripts for ${env} environment...`);
    // Check if scripts are already loaded
    if (document.querySelector('script[src*="adp-web-analytics-sdk"]')
      && document.querySelector('script[src*="conversion-web-analytics-utils"]')) {
      console.info('CJS scripts already loaded, skipping...');
      return Promise.resolve();
    }

    // Get environment-specific script URLs

    const scriptUrls = this.getCJSScriptUrls(env);

    return new Promise((resolve, reject) => {
      const scripts = [
        { name: 'adp-web-analytics-sdk', url: scriptUrls.adpSdk },
        { name: 'conversion-web-analytics-utils', url: scriptUrls.cjsUtils },
      ];

      let scriptsLoaded = 0;
      const errors = [];

      const checkCompletion = () => {
        if (scriptsLoaded === scripts.length) {
          if (errors.length === scripts.length) {
            reject(new Error(`All CJS scripts failed to load: ${errors.join(', ')}`));
          } else if (errors.length > 0) {
            console.warn('Some CJS scripts failed to load:', errors);
            resolve();
          } else {
            console.info(`CJS scripts loaded successfully for ${env} environment`);
            resolve();
          }
        }
      };

      scripts.forEach(({ name, url }) => {
        this.loadScript(
          url,
          () => {
            scriptsLoaded += 1;
            checkCompletion();
          },
          () => {
            errors.push(name);
            scriptsLoaded += 1;
            checkCompletion();
          },
        );
      });
      console.info(`Loading CJS SDK scripts dynamically for ${env} environment...`);
    });
  }

  /**
   * Get environment-specific CJS script URLs
   */
  // eslint-disable-next-line class-methods-use-this
  getCJSScriptUrls(env) {
    const cjsEnv = env || 'stg'; // Default to staging

    // Get CDN configuration from environment constants
    const adpCdn = getEnvironmentConstant({ key: 'CJS_ADP_CDN', env: cjsEnv }) || 'ase-cdn-stg';
    const swcCdn = getEnvironmentConstant({ key: 'CJS_SWC_CDN', env: cjsEnv }) || 'swc-stg';

    return {
      adpSdk: `https://${adpCdn}.autodesk.com/adp/v3/latest/js/@adsk/adp-web-analytics-sdk.min.js`,
      cjsUtils: `https://${swcCdn}.autodesk.com/web-utils/conversion-web-analytics-utils/main.js`,
    };
  }

  /**
   * Wait for CJS SDK to be available after scripts are loaded
   */
  // eslint-disable-next-line class-methods-use-this
  async waitForCJSSDK() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait (50 * 100ms = 5000ms)
      const checkSDK = () => {
        attempts += 1;
        if (window.Adp && window.CjsTracker) {
          console.info('CJS SDK loaded successfully after', attempts * 100, 'ms');
          resolve();
        } else if (attempts >= maxAttempts) {
          console.warn('CJS SDK not available after 5 seconds. Available globals:', {
            Adp: !!window.Adp,
            CjsTracker: !!window.CjsTracker,
            adpWebSdk: !!window.adpWebSdk,
            cjsWebSdk: !!window.cjsWebSdk,
          });
          reject(new Error('CJS SDK not loaded after 5 seconds'));
        } else {
          setTimeout(checkSDK, 100);
        }
      };
      checkSDK();
    });
  }

  /**
   * Initialize CJS Tracker with minimal Auto-Capture configuration
   */
  async initializeCjsTracker(env) {
    try {
      // Get configuration from environment or data attributes
      const adpSdkEnv = document.documentElement.getAttribute('data-env') || env;
      const clientId = getEnvironmentConstant({ key: 'FORGE_CLIENT_ID', env })
        || window?.adsk?.adpSdkInfo?.clientId;

      if (!clientId) {
        throw new Error('CJS Auto-Capture requires FORGE_CLIENT_ID');
      }

      // Initialize CJS Tracker with ADP
      this.cjsTracker = await window.CjsTracker.init(window.Adp())
        .setEnv(adpSdkEnv)
        .load({ id: clientId });

      // Set minimal default event attributes for Auto-Capture
      this.cjsTracker.defaultEventAttributes({
        event_generator: getEnvironmentConstant({ key: 'CJS_EVENT_GENERATOR', env }) || 'learn-one-spa',
      });

      // Set minimal default event data for Auto-Capture
      this.cjsTracker.defaultEventData({
        page: {
          page_category: 'learning',
          page_locale: this.getPageLocale(),
        },
      });

      // Set up Auto-Capture event data hooks
      this.setupAutoCapture(env);

      console.debug('CJS Tracker initialized for Auto-Capture');
    } catch (error) {
      console.error('Failed to initialize CJS Tracker:', error);
      throw error;
    }
  }

  /**
   * Set up Auto-Capture event data hooks
   * @param {string} env - Environment
   */
  setupAutoCapture(env) {
    // Add event data hook for page view events (Auto-Capture)
    this.cjsTracker.addEventDataHook('page_view', () => ({
      page: {
        page_category: this.getPageCategory(),
        page_locale: this.getPageLocale(),
        page_url: window.location.href,
        page_title: document.title,
        page_path: window.location.pathname,
        page_referrer: document.referrer,
      },
    }));

    // Add event data hook for link click events (Auto-Capture)
    this.cjsTracker.addEventDataHook('link_click', () => ({
      page: {
        page_category: this.getPageCategory(),
        page_locale: this.getPageLocale(),
      },
    }));

    // Auto page capture is enabled by default in CJS SDK
    // Auto link capture can be enabled if needed
    const autoLinkCapture = getEnvironmentConstant({ key: 'CJS_AUTO_LINK_CAPTURE', env }) === 'true';
    if (autoLinkCapture && window.adsk?.cjs?.trackClickElements) {
      // Enable auto capture for all click events (no parameters = all elements)
      window.adsk.cjs.trackClickElements();
      console.debug('Auto link click capture enabled for all elements');
    }
  }

  /**
   * Get current page category based on URL
   */
  // eslint-disable-next-line class-methods-use-this
  getPageCategory() {
    const path = window.location.pathname;
    if (path.includes('/tutorial/')) return 'tutorial';
    if (path.includes('/course/')) return 'course';
    if (path.includes('/collection/')) return 'collection';
    if (path.includes('/catalog')) return 'catalog';
    if (path.includes('/coaching')) return 'coaching';
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/learning-plan')) return 'learning-plan';
    if (path === '/learn' || path === '/') return 'home';
    return 'learning';
  }

  /**
   * Get page locale
   */
  // eslint-disable-next-line class-methods-use-this
  getPageLocale() {
    return document.documentElement.lang || 'en_US';
  }

  /**
   * Get status for debugging
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      cjsAvailable: !!(window.adsk?.cjs),
      adpAvailable: !!(window.Adp),
      cjsTrackerAvailable: !!(window.CjsTracker),
    };
  }
}

// Create singleton instance
const cjsAutoCapture = new CJSAutoCapture();

export default cjsAutoCapture;
